import React, { useState, useEffect } from "react";
import axios from "axios";
import "./App.css";
import { translations } from "./translations";
import CyberMatrixHero from "./components/ui/cyber-matrix-hero";
import { Globe } from "./components/ui/globe";
import { HudButton } from "./components/ui/hud-button";
import { CyberCursor } from "./components/ui/cyber-cursor";
import { ProjectModal } from "./components/ui/project-modal";
import { TeamSection } from "./components/ui/team-section";
import PartnerLogos from "./components/ui/partner-logos";

// Language Context Helper
const useLanguage = () => {
  // Initialize language from browser settings or default to 'en'
  const [lang, setLang] = useState(() => {
    if (typeof window === 'undefined') return "en";
    
    const browserLangs = navigator.languages || [navigator.language];
    
    for (const browserLang of browserLangs) {
      const code = browserLang.toLowerCase().split('-')[0];
      if (code === 'ru') return "ru";
      if (code === 'kk' || code === 'kz') return "kz";
    }
    
    return "en";
  });
  
  const toggleLang = (target) => {
    if (target) setLang(target);
    else setLang(prev => prev === "en" ? "ru" : "en");
  };

  return { lang, t: translations[lang], setLang: toggleLang };
};

// --- Components defined OUTSIDE to prevent re-renders ---

import { ScrollReveal } from "./components/ui/scroll-reveal";

const Header = ({ t, lang, setLang, isScrolled }) => (
  // Header is generally fixed, so we might want a simple entry animation or keep it fixed.
  // Using ScrollReveal here creates an entry effect on load since it's at the top.
  <ScrollReveal className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? "bg-dark/90 backdrop-blur-md py-3 shadow-md" : "bg-transparent py-5"}`}>
    <div className="w-full px-6 md:px-12 flex justify-between items-center">
      <a href="/" className="text-2xl font-bold text-white flex items-center group">
        <span className="text-amber group-hover:text-white transition-colors">next</span>
        <span className="text-amber font-mono text-sm align-top group-hover:text-green-400">³</span>
      </a>

      {/* Desktop Nav - CLI Style */}
      <nav className="hidden md:flex items-center space-x-6 font-mono text-sm">
        <a href="#projects" className="text-white/70 hover:text-amber transition-colors">
          <span className="text-green-400">$</span> cd /projects
        </a>
        <a href="#stack" className="text-white/70 hover:text-amber transition-colors">
          <span className="text-green-400">$</span> cat stack.json
        </a>
        <a href="#team" className="text-white/70 hover:text-amber transition-colors">
          <span className="text-green-400">$</span> whoami --all
        </a>
        <a href="#contact-info" className="text-white/70 hover:text-amber transition-colors">
          <span className="text-green-400">$</span> ./contact
        </a>

        
        <div className="flex items-center space-x-4 ml-6">
          <button 
              onClick={() => setLang("en")} 
              className={`text-xs ${lang === 'en' ? 'text-amber font-bold' : 'text-white/40 hover:text-white'}`}
          >EN</button>
          <span className="text-white/20">|</span>
          <button 
              onClick={() => setLang("ru")} 
              className={`text-xs ${lang === 'ru' ? 'text-amber font-bold' : 'text-white/40 hover:text-white'}`}
          >RU</button>
          <span className="text-white/20">|</span>
           <button 
              onClick={() => setLang("kz")} 
              className={`text-xs ${lang === 'kz' ? 'text-amber font-bold' : 'text-white/40 hover:text-white'}`}
          >KZ</button>
        </div>

        <HudButton onClick={() => window.location.href = '#contact'} variant="primary" className="ml-4 scale-90 hidden md:block">
           {t.nav.contact}
        </HudButton>
      </nav>
    </div>
  </ScrollReveal>
);

const Hero = ({ t }) => (
  <section className="min-h-screen flex items-center relative overflow-hidden pt-20">
    <div className="container relative z-10">
      <div className="max-w-5xl mx-auto">
        {/* Status Indicators */}
        <ScrollReveal delay={0.2} className="flex flex-col md:flex-row gap-3 mb-8 text-xs font-mono">
           <div className="bg-black/40 backdrop-blur-md border border-green-500/30 text-green-400 px-3 py-1 rounded inline-flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
              {t.hero.status.google}
           </div>
           <div className="bg-black/40 backdrop-blur-md border border-blue-500/30 text-blue-400 px-3 py-1 rounded inline-flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
              {t.hero.status.hub}
           </div>
        </ScrollReveal>

        <ScrollReveal delay={0.4} className="terminal-console-block p-8 bg-black/20 backdrop-blur-sm border border-white/5 shadow-2xl">
          <div className="console-header flex items-center mb-6 border-b border-white/10 pb-4">
            <span className="text-green-400 mr-2">root@next3-lab</span>
            <span className="text-white/50">:~</span>
            <span className="ml-auto flex space-x-2">
              <span className="h-3 w-3 rounded-full bg-red-500"></span>
              <span className="h-3 w-3 rounded-full bg-yellow-500"></span>
              <span className="h-3 w-3 rounded-full bg-green-500"></span>
            </span>
          </div>

          <div className="font-mono text-sm md:text-base space-y-2 mb-8 text-white/80">
              <p className="typing-effect delay-100">{t.hero.console.system}</p>
              <p className="typing-effect delay-300">{t.hero.console.infra}</p>
              <p className="typing-effect delay-500">{t.hero.console.protocol}</p>
               <p className="typing-effect delay-700">{t.hero.console.framework}</p>
          </div>

          <h1 className="font-mono font-bold text-4xl md:text-6xl mb-6 leading-tight">
            <span className="text-white">{t.hero.title}</span>
            <br/>
            <span className="text-amber neon-number">{t.hero.subtitle}</span>
          </h1>

          <div className="flex flex-col sm:flex-row gap-4 mt-10">
            <HudButton onClick={() => window.location.href = '#projects'}>
              {t.hero.cta_primary}
            </HudButton>

          </div>
        </ScrollReveal>
      </div>
    </div>
  </section>
);

const Projects = ({ t, onOpenModal }) => (
  <section id="projects" className="py-20 relative">
     <div className="container relative z-10">
        <ScrollReveal className="flex items-center mb-12">
          <h2 className="text-3xl font-mono font-bold text-white">
              <span className="text-green-400 mr-2">$</span>
              ls -la ./projects
          </h2>
          <div className="flex-grow h-px bg-white/10 ml-6"></div>
        </ScrollReveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
           {/* NEMESIS */}
           <ScrollReveal delay={0.1} className="group relative bg-black/20 backdrop-blur-sm border border-white/5 hover:border-amber/50 transition-all duration-300 p-1 cursor-pointer" onClick={() => onOpenModal('nemesis')}>
              <div className="absolute inset-0 bg-gradient-to-b from-amber/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="p-6 relative z-10 h-full flex flex-col">
                  <div className="text-amber font-mono text-xs mb-2">[OSINT_FUSION]</div>
                  <h3 className="text-2xl font-bold text-white mb-2">{t.projects.nemesis.title}</h3>
                  <div className="w-12 h-1 bg-amber mb-4"></div>
                  <p className="text-white/60 mb-6 flex-grow font-mono text-sm">{t.projects.nemesis.desc}</p>
                  <div className="border-t border-white/10 pt-4 mt-auto flex flex-col gap-2">
                      <div className="text-xs text-green-400 font-mono">LangGraph • Neo4j • LightRAG • Zingg</div>
                      <span className="text-xs text-white/30 font-mono group-hover:text-amber transition-colors text-center mt-2">[ CLICK FOR DETAILS ]</span>
                  </div>
              </div>
           </ScrollReveal>

           {/* DENTAL */}
           <ScrollReveal delay={0.2} className="group relative bg-black/20 backdrop-blur-sm border border-white/5 hover:border-emerald-500/50 transition-all duration-300 p-1 cursor-pointer" onClick={() => onOpenModal('dental')}>
              <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
               <div className="p-6 relative z-10 h-full flex flex-col">
                  <div className="text-emerald-400 font-mono text-xs mb-2">[SMART_CLINIC]</div>
                  <h3 className="text-2xl font-bold text-white mb-2">{t.projects.dental.title}</h3>
                  <div className="w-12 h-1 bg-emerald-500 mb-4"></div>
                  <p className="text-white/60 mb-6 flex-grow font-mono text-sm">{t.projects.dental.desc}</p>
                   <div className="border-t border-white/10 pt-4 mt-auto flex flex-col gap-2">
                      <div className="text-xs text-green-400 font-mono">Gemma 3 • Gemini 3 Pro • MedSAM • YOLO</div>
                      <span className="text-xs text-white/30 font-mono group-hover:text-amber transition-colors text-center mt-2">[ CLICK FOR DETAILS ]</span>
                  </div>
              </div>
           </ScrollReveal>

           {/* ACCOUNTANT */}
            <ScrollReveal delay={0.3} className="group relative bg-black/20 backdrop-blur-sm border border-white/5 hover:border-purple-500/50 transition-all duration-300 p-1 cursor-pointer" onClick={() => onOpenModal('accountant')}>
              <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
               <div className="p-6 relative z-10 h-full flex flex-col">
                  <div className="text-purple-400 font-mono text-xs mb-2">[B2B_FINANCE]</div>
                  <h3 className="text-2xl font-bold text-white mb-2">{t.projects.accountant.title}</h3>
                  <div className="w-12 h-1 bg-purple-500 mb-4"></div>
                  <p className="text-white/60 mb-6 flex-grow font-mono text-sm">{t.projects.accountant.desc}</p>
                   <div className="border-t border-white/10 pt-4 mt-auto flex flex-col gap-2">
                      <div className="text-xs text-green-400 font-mono">{t.projects.accountant.tech}</div>
                      <span className="text-xs text-white/30 font-mono group-hover:text-amber transition-colors text-center mt-2">[ CLICK FOR DETAILS ]</span>
                  </div>
              </div>
           </ScrollReveal>
        </div>
     </div>
  </section>
);

const TechStack = ({ t }) => (
   <section id="stack" className="py-20 relative overflow-hidden">
      <div className="container relative z-10">
          <div className="flex flex-col lg:flex-row gap-12 items-center">
              <div className="lg:w-1/2">
                  <ScrollReveal>
                    <h2 className="text-3xl font-mono font-bold text-white mb-8">
                        <span className="text-amber">./stack_config.json</span>
                    </h2>
                  </ScrollReveal>
                  
                  <ScrollReveal delay={0.2} className="bg-black/20 backdrop-blur-md border border-white/10 rounded-lg overflow-hidden">
                      <table className="w-full text-left border-collapse">
                          <thead>
                              <tr className="bg-white/5 border-b border-white/10 text-xs uppercase text-white/50 font-mono">
                                  <th className="p-4">{t.stack.columns.tech}</th>
                                  <th className="p-4">{t.stack.columns.app}</th>
                              </tr>
                          </thead>
                          <tbody className="font-mono text-sm">
                              {t.stack.items.map((item, idx) => (
                                  <tr key={idx} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                                      <td className="p-4 text-amber">{item.tech}</td>
                                      <td className="p-4 text-white/70">{item.app}</td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  </ScrollReveal>
              </div>

              <div className="lg:w-1/2 flex justify-center relative overflow-visible">
                  <div className="absolute inset-0 bg-amber/5 blur-3xl rounded-full"></div>
                  <div className="relative z-10 animate-globe-destruction w-full flex justify-center overflow-visible">
                      <Globe />
                  </div>
              </div>
          </div>
      </div>
   </section>
);

const useTypewriter = (phrases, speed = 100, pause = 2000) => {
  const [placeholder, setPlaceholder] = React.useState("");
  const [isFocused, setIsFocused] = React.useState(false);
  const indexRef = React.useRef({ phrase: 0, char: 0, deleting: false });

  React.useEffect(() => {
    let timeout;
    const animate = () => {
      if (isFocused) {
          setPlaceholder("");
          return;
      }

      const { phrase, char, deleting } = indexRef.current;
      const currentPhrase = phrases[phrase];

      // Safety check: if phrase is undefined, reset and retry
      if (!currentPhrase) {
        indexRef.current = { phrase: 0, char: 0, deleting: false };
        timeout = setTimeout(animate, 100);
        return;
      }

      if (deleting) {
        setPlaceholder(currentPhrase.substring(0, char));
        indexRef.current.char--;
        if (indexRef.current.char < 0) {
          indexRef.current.deleting = false;
          indexRef.current.phrase = (phrase + 1) % phrases.length;
          indexRef.current.char = 0;
          timeout = setTimeout(animate, 500);
          return;
        }
        timeout = setTimeout(animate, 50);
      } else {
        setPlaceholder(currentPhrase.substring(0, char + 1));
        indexRef.current.char++;
        if (indexRef.current.char === currentPhrase.length) {
          indexRef.current.deleting = true;
          timeout = setTimeout(animate, pause);
          return;
        }
        timeout = setTimeout(animate, speed + Math.random() * 50); // Slight random typing speed
      }
    };

    timeout = setTimeout(animate, speed);
    return () => clearTimeout(timeout);
  }, [phrases, isFocused, speed, pause]);

  return { 
    placeholder, 
    onFocus: () => setIsFocused(true), 
    onBlur: () => setIsFocused(false) 
  };
};

const Contact = ({ t }) => {
  // Placeholder Data
  const idPlaceholders = t.contact.placeholders.names;
  const phonePlaceholders = t.contact.placeholders.phones;
  const emailPlaceholders = t.contact.placeholders.emails || ["neo@matrix.com"];
  const msgPlaceholders = t.contact.placeholders.messages;

  const idTypewriter = useTypewriter(idPlaceholders, 120, 2000);
  const phoneTypewriter = useTypewriter(phonePlaceholders, 100, 2500);
  const emailTypewriter = useTypewriter(emailPlaceholders, 90, 2200);
  const msgTypewriter = useTypewriter(msgPlaceholders, 80, 3000);

  // Form State
  const [name, setName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [msg, setMsg] = React.useState("");
  
  const [formState, setFormState] = React.useState("idle"); // idle, loading, success
  const [logs, setLogs] = React.useState([]);

  const handlePhoneChange = (e) => {
    let val = e.target.value;
    if (val.length === 0) {
        setPhone("");
        return;
    }
    if (!val.startsWith("+")) {
       val = "+" + val; 
    }
    // Allow digits, plus, space, and hyphen. Using 0-9 to avoid regex escaping issues.
    const cleanVal = val.replace(/[^0-9+ \-]/g, ""); 
    if (cleanVal.length > 20) return; // Prevent crazy long input
    setPhone(cleanVal);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formState !== "idle") return;

    // Validate Phone (Min 10 digits)
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) {
        setFormState("loading");
        setLogs([
            "> [INIT] Validating Input Data...",
            "> [ERROR] Phone Number too short (Min 10 digits).",
            "> [ABORT] Mission Protocol Terminated."
        ]);
        setTimeout(() => {
            setFormState("idle");
            setLogs([]);
        }, 3500);
        return;
    }

    setFormState("loading");
    setLogs(["> [INIT] Secure Uplink Requested..."]);

    // Trigger Backend API Sending in Background
    const isDev = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
    const apiUrl = process.env.REACT_APP_API_URL || (isDev ? "http://localhost:3001" : "");

    fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
            name, 
            phone, 
            email,
            message: msg 
        })
    }).then(res => {
        if (!res.ok) console.error("API Error:", res.statusText);
    }).catch(err => console.error("Network Error:", err));

    const steps = [
        { d: 800, msg: `> [AUTH] Node Verified (ID: ${Math.random().toString(36).substr(2, 6).toUpperCase()})... OK` },
        { d: 1800, msg: `> [ENCRYPT] Encrypting Payload (AES-256-GCM)... OK` },
        { d: 2800, msg: `> [NETWORK] Establishing P2P Tunnel to Core... OK` },
        { d: 3800, msg: `> [UPLOAD] Transmitting Data Packet... 100%` },
        { d: 4500, msg: `> [SUCCESS] Mission Protocol Active.` }
    ];

    steps.forEach(({ d, msg }) => {
        setTimeout(() => setLogs(prev => [...prev, msg]), d);
    });

    setTimeout(() => {
        setFormState("success");
    }, 5000);

    // Reset form after delay
    setTimeout(() => {
        setFormState("idle");
        setPhone("");
        setName("");
        setEmail("");
        setMsg("");
        setLogs([]);
    }, 8000);
  };

  return (
      <section id="contact" className="py-24 relative">
          <div className="container max-w-5xl">
            <div className="flex flex-col gap-16">
               
               {/* 1. Form Section (Moved to Top) */}
               <ScrollReveal className="terminal-console-block p-8 md:p-12 bg-black/20 backdrop-blur-sm border border-white/5 shadow-2xl relative overflow-hidden rounded-xl">
                   {/* Overlay for Loading/Success */}
                   {formState !== "idle" && (
                       <div className="absolute inset-0 bg-black/90 backdrop-blur-md z-20 flex flex-col justify-center items-start p-8 font-mono text-sm md:text-base">
                           {logs.map((log, i) => (
                               <div key={i} className="mb-2">
                                   <span className="text-green-500 mr-2">{log.split(' ')[0]}</span>
                                   <span className="text-white/80 typing-effect">{log.split(' ').slice(1).join(' ')}</span>
                               </div>
                           ))}
                           {formState === "success" && (
                               <div className="mt-4 text-amber font-bold animate-pulse">
                                   {"> TRANSMISSION COMPLETE. STAND BY."}
                               </div>
                           )}
                       </div>
                   )}

                   <div className="mb-8 text-center md:text-left">
                       <h3 className="text-xl font-mono text-white/70">
                           <span className="text-green-400 mr-2">./</span>
                           SECURE_UPLINK
                       </h3>
                   </div>

                   <form className="space-y-6" onSubmit={handleSubmit}>
                       <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                           <div>
                               <label className="block text-xs text-green-400 mb-2 font-mono uppercase">{t.contact.form.name}</label>
                               <input 
                                 type="text" 
                                 required
                                 className="w-full bg-black/50 border border-white/10 p-3 text-white focus:border-green-500 focus:outline-none transition-colors font-mono rounded" 
                                 value={name}
                                 onChange={(e) => setName(e.target.value)}
                                 {...idTypewriter}
                               />
                           </div>
                           <div>
                               <label className="block text-xs text-green-400 mb-2 font-mono uppercase">{t.contact.form.phone}</label>
                               <input 
                                 type="text" 
                                 required
                                 className="w-full bg-black/50 border border-white/10 p-3 text-white focus:border-green-500 focus:outline-none transition-colors font-mono rounded" 
                                 value={phone}
                                 onChange={handlePhoneChange}
                                 {...phoneTypewriter}
                               />
                           </div>
                       </div>
                       <div>
                           <label className="block text-xs text-green-400 mb-2 font-mono uppercase">{t.contact.form.email}</label>
                           <input 
                             type="email" 
                             required 
                             className="w-full bg-black/50 border border-white/10 p-3 text-white focus:border-green-500 focus:outline-none transition-colors font-mono rounded" 
                             value={email}
                             onChange={(e) => setEmail(e.target.value)}
                             {...emailTypewriter}
                           />
                       </div>
                       <div>
                            <label className="block text-xs text-green-400 mb-2 font-mono uppercase">{t.contact.form.message}</label>
                           <textarea 
                             rows="4" 
                             required
                             className="w-full bg-black/50 border border-white/10 p-3 text-white focus:border-green-500 focus:outline-none transition-colors font-mono rounded" 
                             value={msg}
                             onChange={(e) => setMsg(e.target.value)}
                             {...msgTypewriter}
                           ></textarea>
                       </div>
                       <div className="text-center pt-4">
                           <HudButton className="w-full md:w-auto px-12" disabled={formState !== "idle"}>
                               {t.contact.form.submit}
                           </HudButton>
                       </div>
                   </form>
               </ScrollReveal>

               {/* 2. Connect to Grid Module (Moved Below) */}
               <ScrollReveal delay={0.2} className="w-full">
                  <div id="contact-info" className="border-t border-white/10 pt-12">
                      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
                          <h2 className="text-3xl font-mono font-bold text-white">
                            <span className="text-green-400 blink">_</span>
                            {t.contact.title}
                          </h2>
                          <div className="hidden md:block h-px flex-grow bg-white/10 mx-6"></div>
                          <div className="inline-block px-3 py-1 bg-green-500/10 border border-green-500/20 rounded-full">
                              <div className="flex items-center space-x-2">
                                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                  <span className="text-green-500 text-xs font-bold tracking-wider">ONLINE (ENCRYPTED)</span>
                              </div>
                          </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-sm font-mono">
                          <div className="group p-6 bg-white/5 border border-white/5 hover:border-amber/30 rounded-lg transition-all duration-300">
                              <h3 className="text-white/50 text-xs mb-2 uppercase tracking-wider flex items-center gap-2">
                                <svg className="w-4 h-4 text-amber" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                HQ COORDINATES
                              </h3>
                              <p className="text-white text-lg">
                                {t.contact.address}
                              </p>
                          </div>
                          
                          <div className="group p-6 bg-white/5 border border-white/5 hover:border-green-400/30 rounded-lg transition-all duration-300">
                              <h3 className="text-white/50 text-xs mb-2 uppercase tracking-wider flex items-center gap-2">
                                <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                NEURAL LINK
                              </h3>
                              <a href={`mailto:${t.contact.email}`} className="text-white text-lg hover:text-green-400 transition-colors block">
                                {t.contact.email}
                              </a>
                          </div>
                      </div>
                  </div>
               </ScrollReveal>

            </div>
          </div>
      </section>
  );
};

const Footer = ({ t }) => (
  <footer className="py-6 bg-black border-t border-white/10 font-mono text-[10px] text-white/30 relative z-10">
      <div className="container px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3">
              {/* Left: Copyright */}
              <div className="text-center md:text-left shrink-0">
                <p className="font-bold text-white/50">{t.footer.copyright}</p>
              </div>

              {/* Center: Partner Logos */}
              <PartnerLogos />

              {/* Right: Powered By */}
              <div className="text-center md:text-right shrink-0">
                <p className="opacity-70">{t.footer.powered}</p>
              </div>
          </div>
      </div>
  </footer>
);

function App() {
  const { lang, t, setLang } = useLanguage();
  const [, setIsMenuOpen] = useState(false); // Kept if needed later, though not used in new design yet
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
        // Only update state if the value actually changes to avoid unnecessary re-renders (though React handles this mostly)
        const scrolled = window.scrollY > 50;
        setIsScrolled((prev) => (prev !== scrolled ? scrolled : prev));
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Modal state for project details
  const [activeModal, setActiveModal] = useState(null);
  
  const openModal = (projectKey) => {
    setActiveModal(t.projects[projectKey]);
  };
  
  const closeModal = () => {
    setActiveModal(null);
  };

  return (
    <div className="App min-h-screen text-white font-sans bg-dark selection:bg-amber selection:text-black relative">
       <CyberCursor disabled={!!activeModal} />
       {/* Global Matrix Background */}
       <div className="fixed inset-0 z-0 pointer-events-none">
          <CyberMatrixHero />
          {/* Gradient fade out at bottom */}
          <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-dark to-transparent"></div>
       </div>

      <Header t={t} lang={lang} setLang={setLang} isScrolled={isScrolled} />
      
      {/* Main Content relative z-10 to sit above matrix */}
      <main className="relative z-10">
        <Hero t={t} />
        <Projects t={t} onOpenModal={openModal} />
        <TechStack t={t} />
        <TeamSection t={t} />
        <Contact t={t} />
      </main>
      
      <Footer t={t} />
      
      {/* Project Detail Modal */}
      <ProjectModal 
        isOpen={!!activeModal} 
        onClose={closeModal} 
        project={activeModal} 
        t={t}
      />
    </div>
  );
}

export default App;
